import { forwardRef, SelectHTMLAttributes, ReactNode } from 'react';

interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectFieldProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'className'> {
  label?: ReactNode;
  error?: string;
  hint?: string;
  required?: boolean;
  options?: Array<SelectOption | string>;
  placeholder?: string;
  className?: string;
  selectClassName?: string;
}

/**
 * SelectField - Dropdown select with label, error, and hint support
 */
const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(
  (
    {
      label,
      error,
      hint,
      required = false,
      id,
      options = [],
      placeholder,
      className = '',
      selectClassName = '',
      ...props
    },
    ref
  ) => {
    const fieldId = id || props.name;

    // Normalize options to { value, label } format
    const normalizedOptions: SelectOption[] = options.map((opt) => {
      if (typeof opt === 'string') {
        return { value: opt, label: opt };
      }
      return {
        value: opt.value ?? (opt as any).id ?? '',
        label: opt.label ?? (opt as any).name ?? opt.value ?? '',
        disabled: opt.disabled,
      };
    });

    const baseSelectStyles =
      'w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-accent-500 bg-white dark:bg-dark-bg text-light-text dark:text-dark-text transition-colors';
    const errorStyles = error
      ? 'border-error focus:ring-error'
      : 'border-light-border dark:border-dark-border';

    return (
      <div className={`space-y-1.5 ${className}`}>
        {label && (
          <label
            htmlFor={fieldId}
            className="block text-sm font-medium text-light-text dark:text-dark-text"
          >
            {label}
            {required && <span className="text-error ml-0.5">*</span>}
          </label>
        )}
        <select
          ref={ref}
          id={fieldId}
          className={`${baseSelectStyles} ${errorStyles} ${selectClassName}`}
          aria-invalid={!!error}
          aria-describedby={error ? `${fieldId}-error` : hint ? `${fieldId}-hint` : undefined}
          {...props}
        >
          {placeholder && <option value="">{placeholder}</option>}
          {normalizedOptions.map((opt) => (
            <option key={opt.value} value={opt.value} disabled={opt.disabled}>
              {opt.label}
            </option>
          ))}
        </select>
        {error && (
          <p id={`${fieldId}-error`} className="text-xs text-error animate-fade-in" role="alert">
            {error}
          </p>
        )}
        {hint && !error && (
          <p id={`${fieldId}-hint`} className="text-xs text-light-muted dark:text-dark-muted">
            {hint}
          </p>
        )}
      </div>
    );
  }
);

SelectField.displayName = 'SelectField';

export default SelectField;
